import { Event, MoreHoriz, MovieCreation } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDeleteEventType } from '~/api/event-types/delete';
import { IconButton } from '~/components/IconButton';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  tableActionButtonProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { NetworkEventTypes__EventType as EventType } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  eventType: EventType;
}

export const Menu = ({ eventType, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<EventType>();
  const eventTypeActionProps = mergeGridActionProps(moreTableActionProps(eventType), cellProps);

  const link = useLink();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteEventType] = useDeleteEventType();

  return (
    <>
      <IconButton
        label={t('pages.event_types.menu.actions_tooltip')}
        color="secondary"
        {...tableActionButtonProps(eventTypeActionProps)}
        size="small"
      >
        <MoreHoriz />
      </IconButton>
      <MuiMenu {...moreMenuProps}>
        <MenuItem
          component={Link}
          to={link(`/event-types/${moreMenuProps.context?.id}/events`)}
          state={true}
        >
          <ListItemIcon>
            <Event />
          </ListItemIcon>
          <ListItemText>{t('common.tabs.events')}</ListItemText>
        </MenuItem>
        <MenuItem
          component={Link}
          to={link(`/event-types/${moreMenuProps.context?.id}/shows`)}
          state={true}
        >
          <ListItemIcon>
            <MovieCreation />
          </ListItemIcon>
          <ListItemText>{t('common.tabs.shows')}</ListItemText>
        </MenuItem>
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!moreMenuProps.context?.canDestroy.value}
          tooltip={
            moreMenuProps.context?.canDestroy.value
              ? t('pages.event_types.menu.delete_tooltip_enabled')
              : moreMenuProps.context?.canDestroy.reasons?.fullMessages.join(', ') ||
                t('pages.event_types.menu.delete_tooltip_disabled')
          }
          onClick={async () => {
            assert(moreMenuProps.context !== undefined, 'Data missing from context');
            if (!(await confirmDelete())) return;
            await deleteEventType({
              variables: {
                id: moreMenuProps.context.id,
              },
            });
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.event_types.menu.delete_confirm_button')}
        deleteConfirm
        prompt={
          <span
            dangerouslySetInnerHTML={{
              __html: t('pages.event_types.menu.delete_confirm_prompt', {
                count: eventType.eventCount,
              }),
            }}
          />
        }
        title={t('pages.event_types.menu.delete_confirm_title')}
      />
    </>
  );
};
